import { useEffect, useState } from 'react';

import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { mergeRegister } from '@lexical/utils';
import {
  $getSelection,
  $isRangeSelection,
  BLUR_COMMAND,
  COMMAND_PRIORITY_LOW,
  SELECTION_CHANGE_COMMAND,
} from 'lexical';

import { useAIContext } from 'lib/common/contexts/AIContext';
import { useLayout } from 'lib/common/contexts/layout/LayoutContext';

import IQTextSelectionPopper from 'lib/common/hooks/useIQTextSelection/components/IQTextSelectionPopper';

export const IQFloatingEmailMenu = () => {
  const [selectedText, setSelectedText] = useState('');
  const [pos, setPos] = useState({ top: 0, left: 0, width: 0, height: 0 });

  const [editor] = useLexicalComposerContext();

  const { isSoftphone } = useLayout();
  const {
    state: { IQEnabled, isIQModalOpen, isActionMenuOpen },
  } = useAIContext();

  const IQPopperEnabled = IQEnabled && isSoftphone;

  const getSelectionRect = () => {
    if (!IQPopperEnabled) {
      return;
    }
    const nativeSelection = window.getSelection();

    if (nativeSelection && nativeSelection.rangeCount > 0) {
      const range = nativeSelection.getRangeAt(0);

      const rect = range.getBoundingClientRect();
      setPos({
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height,
      });
    }
  };

  const updateEditorSelection = () => {
    const selection = $getSelection();
    const isRange = $isRangeSelection(selection);

    if (isRange) {
      const selectedText = selection?.getTextContent()?.trim() || '';
      if (selectedText.length <= 0) {
        setSelectedText('');
      }

      getSelectionRect();
      setSelectedText(selectedText);
    }
  };

  useEffect(() => {
    if (!IQPopperEnabled) {
      return;
    }

    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          if (isIQModalOpen) {
            return false;
          }
          updateEditorSelection();
        });
      }),
      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        (_payload) => {
          if (isIQModalOpen) {
            return false;
          }
          updateEditorSelection();
          return false;
        },
        COMMAND_PRIORITY_LOW
      ),
      editor.registerCommand(
        BLUR_COMMAND,
        (_payload) => {
          //if either menu is open do not call the deblur update as we don't need to hide the menu
          if (isIQModalOpen || isActionMenuOpen) {
            return false;
          }
          setSelectedText('');
          return false;
        },
        COMMAND_PRIORITY_LOW
      )
    );
  }, [editor, updateEditorSelection]);

  const replaceTextCallback = (text: string) => {
    editor.update(() => {
      const selection = $getSelection();

      if ($isRangeSelection(selection)) {
        selection.removeText(); // Remove the currently selected text
        selection.insertText(text); // Insert the new text
      }
      setSelectedText('');
    });
  };

  if (!IQPopperEnabled) {
    return null;
  }

  const popperPosition = isIQModalOpen ? null : pos;

  return (
    <IQTextSelectionPopper
      popperPosition={popperPosition}
      selectedText={selectedText}
      replaceTextCallback={replaceTextCallback}
      onClosePopper={() => {
        //not needed for email version possibly in the future
      }}
    />
  );
};
